import React, { useEffect, useState } from "react";
import {
  getAllHybridBalanceWithdrawals,
  getPendingHybridBalanceWithdrawals,
  approveHybridBalanceWithdrawals,
  approveHybridBalanceWithdrawalsManually,
  rejectHybridBalanceWithdrawals,
} from "../../../services/Admin";
import formatDate from "../../../utils/formatDate";
import Loader from "../../components/loader/Loader";
import Loader2 from "../../components/loader/Loader2";
import CustomTableCheckbox from "../../components/custom-table/CustomTableCheckbox";
import CustomTable from "../../components/custom-table/CustomTable";
import { toast } from "react-toastify";
import "./style.css";

function AllHybridBalanceWithdrawals() {
  const [isLoading, setIsLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [pendingList, setPendingList] = useState([]);
  const [historyList, setHistoryList] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [activeTab, setActiveTab] = useState("request");
  const [totalWithdrawals, setTotalWithdrawals] = useState(0);
  const [todayWithdrawals, setTodayWithdrawals] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);

  const columns = [
    { Header: "S.no", accessor: "sno" },
    { Header: "User ID", accessor: "userId" },
    { Header: "Name", accessor: "userName" },
    { Header: "Email", accessor: "userEmail" },
    { Header: "Amount", accessor: "amount" },
    { Header: "Wallet Address", accessor: "walletAddress" },
    { Header: "Account Number", accessor: "accountNumber" },
    { Header: "IFSC Code", accessor: "ifscCode" },
    { Header: "Remark", accessor: "transactionRemark" },
    { Header: "Date", accessor: "date" },
  ];

  const historyColumns = [
    { Header: "S.no", accessor: "sno" },
    { Header: "User ID", accessor: "userId" },
    { Header: "Name", accessor: "userName" },
    { Header: "Email", accessor: "userEmail" },
    { Header: "Amount", accessor: "amount" },
    { Header: "Remark", accessor: "transactionRemark" },
    { Header: "Status", accessor: "status" },
    { Header: "Txn Hash", accessor: "txHash" },
    { Header: "Date", accessor: "date" },
  ];

  const computeStatsFromAll = (records) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let todayCount = 0;
    let totalAmountValue = 0;

    (records || []).forEach((record) => {
      totalAmountValue += Number(record.debitedAmount) || 0;
      const createdDate = new Date(record.createdAt);
      createdDate.setHours(0, 0, 0, 0);
      if (createdDate.getTime() === today.getTime()) {
        todayCount++;
      }
    });

    setTotalWithdrawals(records?.length || 0);
    setTodayWithdrawals(todayCount);
    setTotalAmount(totalAmountValue);
  };

  const fetchAll = async () => {
    setIsLoading(true);
    try {
      const [allRes, pendingRes] = await Promise.all([
        getAllHybridBalanceWithdrawals(),
        getPendingHybridBalanceWithdrawals(),
      ]);

      computeStatsFromAll(allRes?.data || []);

      const requests = pendingRes?.requests || [];
      const formatted = requests.map((r, i) => ({
        sno: i + 1,
        _id: r._id,
        userId: r.userId || "N/A",
        userName: r.userName || "Unknown",
        userEmail: r.userEmail || "N/A",
        amount: `$${(Number(r.amount) || 0).toFixed(2)}`,
        walletAddress: r.walletAddress || "—",
        accountNumber: r.accountNumber || "-",
        ifscCode: r.ifscCode || "-",
        transactionRemark: r.transactionRemark || "-",
        date: r.createdAt ? formatDate(r.createdAt) : "N/A",
      }));
      setPendingList(formatted);

      const history = (allRes?.data || [])
        .filter((r) => r.status !== "Pending")
        .map((r, i) => {
          let statusColor = "#6b7280";
          if (r.status === "Completed") statusColor = "#10b981";
          else if (r.status === "Rejected") statusColor = "#ef4444";
          else if (r.status === "Failed") statusColor = "#ef4444";

          return {
            sno: i + 1,
            userId: r.userId || "N/A",
            userName: r.userName || "Unknown",
            userEmail: r.userEmail || "N/A",
            amount: `$${(Number(r.debitedAmount) || 0).toFixed(2)}`,
            transactionRemark: r.transactionRemark || "-",
            status: (
              <span style={{ color: statusColor, fontWeight: 500 }}>
                {r.status || "Unknown"}
              </span>
            ),
            txHash: r.txHash ? (
              <a
                href={`https://bscscan.com/tx/${r.txHash}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontWeight: 500 }}
              >
                Hash
              </a>
            ) : (
              "-"
            ),
            date: r.createdAt ? formatDate(r.createdAt) : "N/A",
          };
        });
      setHistoryList(history);
    } catch (err) {
      console.error("fetchAll error:", err);
      toast.error("Failed to load hybrid balance withdrawals");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const onSelectedRows = (selectedFlatRows) => {
    setSelectedRows(selectedFlatRows);
  };

  const handleApprove = async () => {
    if (!selectedRows.length) {
      toast.warn("No rows selected");
      return;
    }
    if (!window.confirm(`Approve ${selectedRows.length} hybrid balance withdrawal(s)?`)) return;

    setProcessing(true);
    try {
      const payloads = selectedRows.map((row) => ({ _id: row.original._id }));
      const res = await approveHybridBalanceWithdrawals(payloads);
      const processed = res?.processedPayouts?.length || 0;
      const failed = res?.failedPayouts?.length || 0;
      if (processed > 0) toast.success(`${processed} approved`);
      if (failed > 0) {
        toast.error(`${failed} failed`);
        console.error("Failed payouts:", res.failedPayouts);
      }
      await fetchAll();
    } catch (err) {
      console.error(err);
      toast.error("Error approving withdrawals");
    } finally {
      setProcessing(false);
    }
  };

  const handleApproveManually = async () => {
    if (!selectedRows.length) {
      toast.warn("No rows selected");
      return;
    }
    if (!window.confirm(`Approve ${selectedRows.length} hybrid balance withdrawal(s) manually through bank account? No USDT will be sent on-chain.`)) return;

    setProcessing(true);
    try {
      const payloads = selectedRows.map((row) => ({ _id: row.original._id }));
      const res = await approveHybridBalanceWithdrawalsManually(payloads);
      const processed = res?.processedPayouts?.length || 0;
      const failed = res?.failedPayouts?.length || 0;
      if (processed > 0) toast.success(`${processed} approved manually`);
      if (failed > 0) {
        toast.error(`${failed} failed`);
        console.error("Failed payouts:", res.failedPayouts);
      }
      await fetchAll();
    } catch (err) {
      console.error(err);
      toast.error("Error approving withdrawals manually");
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRows.length) {
      toast.warn("No rows selected");
      return;
    }
    if (!window.confirm(`Reject ${selectedRows.length} hybrid balance withdrawal(s)? The locked balance will be refunded.`)) return;

    setProcessing(true);
    try {
      const payloads = selectedRows.map((row) => ({ _id: row.original._id }));
      const res = await rejectHybridBalanceWithdrawals(payloads);
      const processed = res?.processedPayouts?.length || 0;
      const failed = res?.failedPayouts?.length || 0;
      if (processed > 0) toast.success(`${processed} rejected and refunded`);
      if (failed > 0) {
        toast.error(`${failed} failed`);
        console.error("Failed payouts:", res.failedPayouts);
      }
      await fetchAll();
    } catch (err) {
      console.error(err);
      toast.error("Error rejecting withdrawals");
    } finally {
      setProcessing(false);
    }
  };

  if (isLoading) return <Loader />;

  return (
    <div className="investment-history">
      {processing && <Loader2 />}

      <h1 className="main-heading">All Hybrid Balance Withdrawals</h1>
      <br />

      {/* Statistics Cards */}
      <div className="stats-container">
        <div className="stat-card">
          <div className="stat-left">
            <p className="stat-label">Total Withdrawals</p>
            <h2 className="stat-value">{totalWithdrawals}</h2>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-left">
            <p className="stat-label">Today Withdrawals</p>
            <h2 className="stat-value">{todayWithdrawals}</h2>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-left">
            <p className="stat-label">Total Amount</p>
            <h2 className="stat-value">${totalAmount.toFixed(2)}</h2>
          </div>
        </div>
      </div>

      <br />

      <div className="hbw-tabs">
        <button
          className={`hbw-tab${activeTab === "request" ? " active" : ""}`}
          onClick={() => setActiveTab("request")}
          type="button"
        >
          Request ({pendingList.length})
        </button>
        <button
          className={`hbw-tab${activeTab === "history" ? " active" : ""}`}
          onClick={() => setActiveTab("history")}
          type="button"
        >
          History ({historyList.length})
        </button>
      </div>

      {activeTab === "request" ? (
        <>
          <div className="button-group" style={{ margin: "1rem 0" }}>
            <button
              onClick={handleReject}
              disabled={processing || !selectedRows.length}
              style={{ background: "red", opacity: !selectedRows.length ? 0.6 : 1 }}
            >
              Reject Selected
            </button>
            <button
              onClick={handleApproveManually}
              disabled={processing || !selectedRows.length}
              style={{ background: "#2563eb", marginLeft: "0.5rem", opacity: !selectedRows.length ? 0.6 : 1 }}
            >
              Approve Manually
            </button>
            <button
              onClick={handleApprove}
              disabled={processing || !selectedRows.length}
              style={{ background: "green", marginLeft: "0.5rem", opacity: !selectedRows.length ? 0.6 : 1 }}
            >
              Approve Selected
            </button>
          </div>

          <CustomTableCheckbox
            columns={columns}
            data={pendingList}
            heading={`Pending Hybrid Balance Withdrawals (${pendingList.length})`}
            onSelectedRows={onSelectedRows}
          />
        </>
      ) : (
        <CustomTable
          columns={historyColumns}
          data={historyList}
          heading={`History (${historyList.length})`}
        />
      )}
    </div>
  );
}

export default AllHybridBalanceWithdrawals;
